<template>
    <transition>
    <div class="container-fluid page-content">
        <!-- Modern Page Header -->
        <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center page-header-row gap-3">
            <div>
                <h4 class="page-title text-slate-800 fw-bold mb-1">Product Category</h4>
                <nav aria-label="breadcrumb">
                    <ol class="breadcrumb mb-0">
                        <li class="breadcrumb-item"><a href="/" class="text-muted"><i class="fas fa-home me-1"></i>Home</a></li>
                        <li class="breadcrumb-item"><a href="javascript:void(0);" class="text-muted">Product Settings</a></li>
                        <li class="breadcrumb-item active text-indigo fw-medium" aria-current="page">Categories</li>
                    </ol>
                </nav>
            </div>
            <div>
                <button type="button" class="btn btn-premium d-flex align-items-center gap-2" @click="toggleModal()">
                    <i class="fas fa-plus"></i> Add New Category
                </button>
            </div>
        </div>

        <!-- Modern Filter Dashboard Bar -->
        <div class="card border-0 shadow-sm filter-card">
            <div class="card-body p-3">
                <div class="row align-items-center g-2">
                    <div class="col-md-4 col-sm-6">
                        <div class="form-group mb-0 d-flex align-items-center">
                            <label class="form-label-custom text-muted me-2 mb-0 text-nowrap"><i class="fas fa-building me-1"></i>Company</label>
                            <select class="form-select-custom w-100" v-model="tableData.company_id" @change="fetchItems()">
                                <option value="">--- All Companies ---</option>
                                <option v-for="(company, i) in companies" :key="i" :value="company.id">{{ company.name }}</option>
                            </select>
                        </div>
                    </div>
                    <div class="col-md-8 col-sm-6 d-flex flex-column flex-sm-row justify-content-sm-end align-items-sm-center gap-3">
                        <div class="d-flex align-items-center gap-2">
                            <span class="text-muted text-nowrap">Show</span>
                            <select class="form-select-custom length-select" v-model="tableData.length" @change="fetchItems()">  
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                            <span class="text-muted text-nowrap">entries</span>
                        </div>
                        <div class="position-relative search-container">
                            <i class="fas fa-search search-icon text-muted"></i>
                            <input type="text" class="form-control-custom search-input" v-model="tableData.search" placeholder="Search categories..." @input="fetchItems()">
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modern Table List Card -->
        <div class="card border-0 shadow-sm table-card">
            <div class="card-body p-0">
                <div class="table-responsive-custom">
                    <table class="table-premium mb-0" v-if="!loading && items.length > 0">
                        <thead>
                            <tr>
                                <th class="text-center" style="width: 5%;">SL</th>
                                <th class="text-start" @click="sortBy('name', tableData.dir === 'asc' ? 'desc' : 'asc')" style="width: 20%; cursor: pointer;">
                                    Category Name
                                    <i class="fas fa-sort ms-1 text-muted" :class="{'fa-sort-up text-indigo': tableData.sortKey === 'name' && tableData.dir === 'asc', 'fa-sort-down text-indigo': tableData.sortKey === 'name' && tableData.dir === 'desc'}"></i>
                                </th>
                                <th class="text-start" style="width: 15%;">Company</th>
                                <th class="text-start" @click="sortBy('parent_cat_name', tableData.dir === 'asc' ? 'desc' : 'asc')" style="width: 15%; cursor: pointer;">
                                    Parent Category
                                    <i class="fas fa-sort ms-1 text-muted" :class="{'fa-sort-up text-indigo': tableData.sortKey === 'parent_cat_name' && tableData.dir === 'asc', 'fa-sort-down text-indigo': tableData.sortKey === 'parent_cat_name' && tableData.dir === 'desc'}"></i>
                                </th>
                                <th class="text-center" style="width: 12%;">Image</th>
                                <th class="text-center" @click="sortBy('discount', tableData.dir === 'asc' ? 'desc' : 'asc')" style="width: 10%; cursor: pointer;">
                                    Discount
                                    <i class="fas fa-sort ms-1 text-muted" :class="{'fa-sort-up text-indigo': tableData.sortKey === 'discount' && tableData.dir === 'asc', 'fa-sort-down text-indigo': tableData.sortKey === 'discount' && tableData.dir === 'desc'}"></i>
                                </th>
                                <th class="text-start" @click="sortBy('description', tableData.dir === 'asc' ? 'desc' : 'asc')" style="width: 15%; cursor: pointer;">
                                    Description
                                    <i class="fas fa-sort ms-1 text-muted" :class="{'fa-sort-up text-indigo': tableData.sortKey === 'description' && tableData.dir === 'asc', 'fa-sort-down text-indigo': tableData.sortKey === 'description' && tableData.dir === 'desc'}"></i>
                                </th>
                                <th class="text-center" style="width: 8%;">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, i) in items" :key="i">
                                <td class="text-center text-muted fw-semibold">
                                    {{ (pagination && pagination.currentPage ? (pagination.currentPage - 1) * tableData.length : 0) + i + 1 }}
                                </td>
                                <td class="text-start">
                                    <span class="fw-bold text-slate-800">{{ item.name }}</span>
                                    <span v-if="item.is_featured == 1" class="badge badge-featured ms-2"><i class="fas fa-star me-1"></i>Featured</span>
                                </td>
                                <td class="text-start text-muted">
                                    {{ item.company ? item.company.name : '—' }}
                                </td>
                                <td class="text-start text-muted">
                                    {{ item.parents ? item.parents.name : '—' }}
                                </td>
                                <td class="text-center">
                                    <div class="image-avatar-container d-inline-block">
                                        <img :src="item.img_url+'/'+item.image" class="image-avatar" v-if="item.image" alt="Category Image">
                                        <div class="image-avatar-placeholder" v-else>
                                            <i class="fas fa-folder text-muted"></i>
                                        </div>
                                    </div>
                                </td>
                                <td class="text-center">
                                    <span v-if="parseFloat(item.discount) > 0" class="badge badge-discount">
                                        {{ item.discount }}% Off
                                    </span>
                                    <span v-else class="text-muted">—</span>
                                </td>
                                <td class="text-start text-muted text-truncate-custom" :title="item.description">
                                    {{ item.description || '—' }}
                                </td>
                                <td class="text-center">
                                    <div class="dropdown">
                                        <button class="btn btn-icon" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                            <i class="fas fa-ellipsis-v"></i>
                                        </button>
                                        <ul class="dropdown-menu dropdown-menu-end border-0 shadow-lg dropdown-premium">
                                            <li>
                                                <a class="dropdown-item text-primary" href="javascript:void(0);" @click="edit(item)">
                                                    <i class="fas fa-edit me-2"></i>Edit Category
                                                </a>
                                            </li>
                                            <li><hr class="dropdown-divider"></li>
                                            <li>
                                                <a class="dropdown-item text-danger" href="javascript:void(0);" @click.prevent="deleteItem(item)">
                                                    <i class="fas fa-trash-alt me-2"></i>Remove
                                                </a>
                                            </li>
                                        </ul>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <!-- Fallback State -->
                    <div v-if="!loading && items.length === 0" class="text-center py-5">
                        <img src="../../assets/image/loading.gif" class="mb-3 opacity-25" style="width: 80px; filter: grayscale(1);" />
                        <h5 class="text-muted fw-semibold">No Categories Found</h5>
                        <p class="text-muted mb-0 small">Try adjusting your filters or search keywords.</p>
                    </div>

                    <!-- Loading State -->
                    <div v-if="loading" class="text-center py-5">
                        <div class="spinner-premium mb-3"></div>
                        <h5 class="text-muted fw-semibold">Loading Categories</h5>
                        <p class="text-muted mb-0 small">Please wait a moment...</p>
                    </div>
                </div>

                <!-- Modern Pagination Footer -->
                <div class="card-footer border-0 bg-white py-3 px-4 d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3" v-if="pagination && items.length > 0">
                    <div class="text-muted small">
                        Showing <span class="fw-semibold text-slate-800">{{ pagination.from }}</span> to <span class="fw-semibold text-slate-800">{{ pagination.to }}</span> of <span class="fw-semibold text-slate-800">{{ pagination.total }}</span> entries
                    </div>
                    <Pagination 
                        :pagination="pagination"  
                        :language="lang"
                        @onChangePage="setPage" > 
                    </Pagination>
                </div>
            </div>
        </div>

        <!-- Modern Add/Edit Modal -->
        <Modal @close="toggleModal()" :modalActive="modalActive">
            <div class="modal-content border-0 shadow-lg modal-premium">
                <div class="modal-header"> 
                    <h4 class="fw-bold m-0">{{ editMode ? 'Update Product Category' : 'Create New Category' }}</h4>
                    <button @click="toggleModal()" type="button" class="btn-close-custom">✕</button>
                </div>
                <form @submit.prevent="submitForm()" enctype="multipart/form-data">
                    <div class="modal-body py-4">
                        <div class="row g-4">
                            <div class="col-md-8">
                                <div class="row g-3">
                                    <div class="col-md-6">
                                        <div class="form-group mb-0">
                                            <label for="name" class="form-label-custom text-slate-700 fw-semibold mb-2">Category Name *</label>
                                            <input type="text" class="form-control-custom" :class="{'is-invalid': errors.name}" @keypress="onkeyPress('name')" v-model="form.name" id="name" placeholder="Enter category name" autocomplete="off"> 
                                            <div class="invalid-feedback" v-if="errors.name">
                                                {{errors.name[0]}}
                                            </div>
                                        </div>
                                    </div>  
                                    <div class="col-md-6">
                                        <div class="form-group mb-0">
                                            <label class="form-label-custom text-slate-700 fw-semibold mb-2">Parent Category</label>
                                            <Multiselect 
                                                class="form-control-custom-select" 
                                                :class="{'is-invalid': errors.parent_id}"
                                                mode="single"
                                                v-model="form.parent_id"
                                                placeholder="Select Parent"  
                                                :searchable="true" 
                                                :filter-results="true"
                                                :options="pcategories"
                                                :classes="multiclasses"
                                                :close-on-select="true" 
                                                :min-chars="1"
                                                :resolve-on-load="false" 
                                                :create-option="true" 
                                            />                                                   
                                            <div class="invalid-feedback" v-if="errors.parent_id">
                                                {{errors.parent_id[0]}}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="form-group mt-3">
                                    <label for="description" class="form-label-custom text-slate-700 fw-semibold mb-2">Description</label>
                                    <textarea class="form-control-custom form-textarea" :class="{'is-invalid': errors.description}" @keypress="onkeyPress('description')" v-model="form.description" id="description" placeholder="Describe the category..." rows="3"></textarea>
                                    <div class="invalid-feedback" v-if="errors.description">
                                        {{errors.description[0]}}
                                    </div>
                                </div>

                                <div class="row g-3 mt-1">
                                    <div class="col-md-6">
                                        <div class="form-group mb-0">
                                            <label for="discount" class="form-label-custom text-slate-700 fw-semibold mb-2">Discount (%)</label>
                                            <div class="input-group-custom">
                                                <input type="number" step="any" min="0" max="100" class="form-control-custom pe-5" :class="{'is-invalid': errors.discount}" @keypress="onkeyPress('discount')" v-model="form.discount" id="discount" placeholder="0">
                                                <span class="input-group-addon-right">%</span>
                                            </div>
                                            <div class="invalid-feedback" v-if="errors.discount">
                                                {{errors.discount[0]}}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-md-6 d-flex align-items-end">
                                        <div class="form-check-custom">
                                            <input type="checkbox" class="form-check-input-custom" id="is_featured" v-model="form.is_featured" true-value="1" false-value="0" @change="onkeyPress('is_featured')">
                                            <label class="form-check-label-custom" for="is_featured">Featured Category</label>
                                        </div>
                                    </div>
                                </div>

                                <div class="row g-3 mt-2">
                                    <div class="col-md-6">
                                        <div class="form-group mb-0">
                                            <label class="form-label-custom text-slate-700 fw-semibold mb-2">Category Icon / Image</label>
                                            <div class="file-upload-wrapper">
                                                <button type="button" class="btn btn-outline-file-upload btn-sm">
                                                    <i class="fas fa-camera me-2"></i>Choose Image
                                                </button>
                                                <input type="file" class="file-upload-input" name="..." @change="onImageChange"/>
                                            </div>
                                            <div class="invalid-feedback d-block" v-if="errors.image">
                                                {{errors.image[0]}}
                                            </div>
                                        </div>
                                    </div>

                                    <div class="col-md-6">
                                        <div class="form-group mb-0">
                                            <label for="pcstatus" class="form-label-custom text-slate-700 fw-semibold mb-2">Status</label>
                                            <select class="form-select-custom" v-model="form.status" @change="onkeyPress('status')" id="pcstatus">
                                                <option value="1">Active</option>
                                                <option value="0">Inactive</option>
                                            </select>
                                            <div class="invalid-feedback" v-if="errors.status">
                                                {{errors.status[0]}}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-4 text-center">
                                <label class="form-label-custom text-slate-700 fw-semibold mb-2 d-block text-center">Image Preview</label>
                                <div class="modal-image-preview-container d-inline-block shadow-sm">
                                    <img :src="imagePreview" v-if="form.image && imagePreview" class="modal-image-preview" alt="New Upload Preview">
                                    <img :src="form.img_url+'/'+form.image" v-else-if="editMode && form.image" class="modal-image-preview" alt="Current Image">
                                    <div class="modal-image-placeholder" v-else>
                                        <i class="fas fa-image fa-3x text-muted opacity-25"></i>
                                        <span class="d-block mt-2 small text-muted">No Image Chosen</span>
                                    </div>
                                </div>
                            </div> 
                        </div>
                    </div>
                    <div class="modal-footer border-0 pt-0"> 
                        <button type="button" class="btn btn-outline-premium me-2" @click="toggleModal()">Cancel</button>
                        <button type="submit" class="btn btn-premium" :disabled="disabled">
                            <span v-show="isSubmit" class="spinner-border spinner-border-sm me-2" role="status"></span>
                            {{ editMode ? 'Update Category' : 'Create Category' }}
                        </button>
                    </div>
                </form>
            </div>
        </Modal>
    </div>
    </transition>
</template>

<script> 
import Modal from "./../helper/Modal"; 
import Form from 'vform'
import axios from 'axios';

import Pagination from '@/components/Pagination.vue';

export default {
    name: 'PosLeftbar',
    components: {
        Modal,
        Pagination
    },
    props:{
        language: {
          type: Object,
          default: () => {
            return {
              lengthMenu: null,
              info: null,
              zeroRecords: null, 
              search: null
            }
          },
        },
    },
    data() {
        return {
            loading: true,
            isSubmit: false,
            showModal: false,
            editMode:false,
            disabled: false,
            modalActive:false,
            errors: {},
            imagePreview:'',
            btn:'Create',
            items: [],
            pcategories: [],
            companies: [],
            form: new Form({
                id: '',
                name: '',
                description: '',
                image: '',
                parent_id: 0,
                is_featured: 0,
                discount:0,
                status: 1,
                img_url: ''
            }),
            multiclasses: { 
              clear: '',
              clearIcon: '', 
            },
            columns: [ 
                {
                    label: 'SL',
                    name: '',           
                    width: '5%'
                },  
                {
                    label: 'Category Name',
                    name: 'name',           
                    width: '20%'
                },   
                {
                    label: 'Company',
                    name: 'company_name',           
                    width: '15%'
                },   
                {
                    label: 'Parent Category',
                    name: 'parent_cat_name',
                    width: '15%'
                },
                {
                    label: 'Image',
                    name: 'image',
                    width: '12%'
                },
                {
                    label: 'Discount',
                    name: 'discount',
                    width: '10%'
                },  
                {
                    label: 'Description',
                    name: 'description',
                    width: '15%'
                },
                {
                    label: 'Actions',            
                    name: '',
                    isSearch: false, 
                    isAction: true,
                    width: '8%',
                }
            ],
            tableData: {
                draw: 0,
                length: 10,
                search: '',
                column: 1, 
                dir: 'asc',
                sortKey: 'name', 
                company_id:''
            }, 
            lang: {
                lengthMenu: this.$props.language.lengthMenu ? this.$props.language.lengthMenu : 'Show_MENU_entries',
                info: this.$props.language.info ? this.$props.language.info : 'Showing_FROM_to_TO_of_TOTAL_entries',
                zeroRecords: this.$props.language.zeroRecords ? this.$props.language.zeroRecords : 'No data available in table.', 
                search: this.$props.language.search ? this.$props.language.search : 'Search'
            },
            pagination: {
                lastPage: '',
                currentPage: '',
                total: '',
                lastPageUrl: '',
                nextPageUrl: '',
                prevPageUrl: '',
                from: '',
                to: '',
                links:[],
            }, 
        };
    },
    created() {
        this.fetchItems();
        this.fetchCompanies();
    },
    methods: { 
        toggleModal: function() {
            this.modalActive = !this.modalActive;   
            if(!this.modalActive){
                this.editMode = false;
                this.btn='Create';
                this.imagePreview = '';
            } 
            this.errors = '';
            this.isSubmit = false;
            this.form.reset(); 
        },
        fetchCompanies() {   
            axios.get(this.apiUrl+'/companies', this.headerjson)
            .then((res) => { 
                this.companies = res.data.data; 
            }).catch((err) => { 
                this.$toast.error(err.response.data.message);
            }).finally((ress) => {
                this.loading = false;
            });
        },
        // Funtion For Pagination 
        fetchItems(url = this.apiUrl+'/product_categories/list') { 
            this.tableData.draw++;
            axios.get(url, {params:this.tableData, headers:this.headerparams})
            .then((response) => {
                let data = response.data.data;   
                if(this.tableData.draw = data.draw) { 
                  this.items = data.data.data;
                  this.pcategories = data.data.data.map(({ id, name }) => (
                  { label: name, value: id }
                  )); 
                  this.configPagination(data.data);
                }
            })
            .catch(errors => {
                console.log(errors);
            })
            .finally((fres) => {
                this.loading = false;
            });
        },

        configPagination(data){
            this.pagination.lastPage = data.last_page;
            this.pagination.currentPage = data.current_page;
            this.pagination.total   = data.total ? data.total : 0;
            this.pagination.lastPageUrl = data.last_page_url;
            this.pagination.nextPageUrl = data.next_page_url;
            this.pagination.prevPageUrl = data.prev_page_url;
            this.pagination.from = data.from ? data.from : 0;
            this.pagination.to = data.to ? data.to : 0;  
            this.pagination.links = data.links;
        },

        sortBy(key,sortable) {
            this.tableData.sortKey = key;
            this.tableData.column = this.getIndex(this.columns, 'name', key);
            this.tableData.dir = sortable;
            this.fetchItems();
        },
        setPage(data){  
            this.fetchItems(data.url); 
        },
        getIndex(array, key, value) {
            return array.findIndex(i => i[key] == value)
        },

        filterData(){
          this.fetchItems(); 
        },

        edit: function(item) { 
            this.btn='Update';
            this.editMode = true;
            this.toggleModal();
            this.form.fill(item);  
        },
        submitForm: function(e) { 

            this.isSubmit = true;
            this.disabled = true;
            const formData = new FormData();  
            formData.append('name', this.form.name);            
            formData.append('parent_id', this.form.parent_id);
            formData.append('description', this.form.description);
            formData.append('is_featured', this.form.is_featured);
            formData.append('status', this.form.status);  
            formData.append('discount', this.form.discount);  
            if(this.editMode){
                formData.append('_method', 'put');
                if(this.imagePreview){
                    this.form.image ? formData.append('image', this.form.image, this.form.image.name) : '';
                } 
                var postEvent = axios.post(this.apiUrl+'/product_categories/'+this.form.id, formData, this.headers);
            }else{ 
                this.form.image ? formData.append('image', this.form.image, this.form.image.name) : '';
                var postEvent = axios.post(this.apiUrl+'/product_categories', formData, this.headers);
            }  

            postEvent.then(res => {
                this.isSubmit = false;
                this.disabled = false;
                if(res.status == 200){
                    this.toggleModal(); 
                    this.fetchItems();
                    this.$toast.success(res.data.message); 
                }else{
                    this.$toast.error(res.data.message);
                }
            }).catch(err => { 
                this.isSubmit = false; 
                this.disabled = false;
                this.$toast.error(err.response.data.message);
                if(err.response.status == 422){
                    this.errors = err.response.data.errors 
                }
            });
        },

        onImageChange(e) {
            this.form.image = e.target.files[0]
            let files = e.target.files || e.dataTransfer.files;
            if (!files.length)
                return;  
            
            this.createImage(files[0]);

        },
        createImage(file) {
            let reader = new FileReader();
            let vm = this;
            reader.onload = (e) => { 
                vm.imagePreview = e.target.result;
            };
            reader.readAsDataURL(file);
        },
        validation: function (...fiels){ 
            var obj = new Object(); 
            var validate = false;
            for (var k in fiels){     // Loop through the object  
                for (var j in this.form){  
                    if((j==fiels[k]) && (!this.form[j])) {  
                        obj[fiels[k]] = fiels[k].replace("_", " ")+' field is required';  // Delete obj[key]; 
                        this.errors = {...this.errors, ...obj};
                    }else{
                        validate = false;
                    }
                }              
            }  
        },
        onkeyPress: function(field) { 
            for (var k in this.errors){     // Loop through the object
                if(k==field){      // If the current key contains the string we're looking for 
                    delete this.errors[k];  // Delete obj[key];
                }
            }  
        },
        deleteItem: function(item) {
            this.$swal({
                title: 'Are you sure?',
                text: "You want delete this item!", 
                showCancelButton: true,
                confirmButtonCategory: '#3085d6',
                cancelButtonCategory: '#d33',
                confirmButtonText: 'Yes, delete it!'
            }).then((result) => { 
                if (result.value) { 
                    axios.delete(this.apiUrl+'/product_categories/'+item.id,this.headers) 
                    .then(res => {
                        if(res.status == 200){   
                            this.fetchItems();
                            this.$toast.success(res.data.message); 
                        }else{
                            this.$toast.error(res.data.message);
                        }
                        console.log(res.data)
                    }).catch(err => {  
                        this.$toast.error(err.response.data.message); 
                    }) 
                }
            }); 
        },

    },
    destroyed() {},
    mounted() {
        window.scrollTo(0, 0);
    },
    computed: {}
}
</script>

<style scoped>
/* Text Truncation */
.text-truncate-custom {
    max-width: 180px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Input group */
.input-group-custom {
    position: relative;
    display: flex;
    align-items: stretch;
    width: 100%;
}
.input-group-addon-right {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-weight: 600;
}

/* Custom Checkbox */
.form-check-custom {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 42px;
}
.form-check-input-custom {
    width: 18px;
    height: 18px;
    border: 1.5px solid #cbd5e1;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
}
.form-check-input-custom:checked {
    background-color: #4f46e5;
    border-color: #4f46e5;
}
.form-check-label-custom {
    font-size: 14px;
    color: #334155;
    font-weight: 600;
    margin-bottom: 0;
    cursor: pointer;
    user-select: none;
}
</style>

<style src="@vueform/multiselect/themes/default.css"></style>